import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MTX_DRAWER_DATA, MtxDrawerRef } from '@ng-matero/extensions/drawer';
import { ProvidersService } from '@app/shared/services/providers.service';
import { LoggerService } from '@app/shared/services/logger.service';
import * as _ from 'lodash';
import { BannerNotificationsService } from '@app/shared/services/banner-notifications.service';
import { MAX_MARKUP_DOLLAR, MAX_MARKUP_PERCENT, MIN_MARKUP_VALUE, ShippingProviders } from '../../../../../../shared/constants';

@Component({
  standalone: false,
  selector: 'app-edit-courier-settings',
  templateUrl: './edit-courier-settings.component.html',
})
export class EditCourierSettingsComponent implements OnInit {
  public loading: boolean = true;
  public provider: any;
  public providerSettingsForm: FormGroup;
  public canEditStatus: boolean;

  private readonly accountTypeSwitchControls = ['maEnabled', 'byoaEnabled'];

  constructor(
    public drawerRef: MtxDrawerRef<EditCourierSettingsComponent>,
    @Inject(MTX_DRAWER_DATA)
    public data: { provider: any; canEditStatus: boolean },
    private formBuilder: FormBuilder,
    private providersService: ProvidersService,
    private logger: LoggerService,
    private bannerNotificationsService: BannerNotificationsService
  ) {}

  public get showAccountTypeSwitches(): boolean {
    return this.canEditStatus && this.provider?.providerType === ShippingProviders.UPS;
  }

  ngOnInit() {
    this.loading = true;
    this.provider = this.data.provider;
    this.canEditStatus = this.data.canEditStatus;
    this.setupForm();
    this.loading = false;
  }

  public dismissDrawer(): void {
    this.drawerRef.dismiss(false);
  }

  public updateSettings(): void {
    const providerSettingValues = this.providerSettingsForm.value;

    const markupPercentageValue = providerSettingValues?.markupPercentageValue || null;
    const markupDollarValue = providerSettingValues?.markupDollarValue || null;
    const deliveryDate = providerSettingValues?.deliveryDate || null;
    const metadataValues: { fuelCharge?: number | null; maDisabled?: boolean; byoaDisabled?: boolean } = {};
    const storedFuelCharge = _.get(this.provider, 'metadata.fuelCharge', null);
    // 0 is a real value; only an empty field clears the stored fuel charge
    if (providerSettingValues?.fuelSurchargeMarkupMargin != null) {
      metadataValues.fuelCharge = providerSettingValues.fuelSurchargeMarkupMargin;
    } else if (storedFuelCharge !== null) {
      metadataValues.fuelCharge = null;
    }
    if (this.showAccountTypeSwitches) {
      // Disabled controls are excluded from form.value, so read the raw values.
      const rawValues = this.providerSettingsForm.getRawValue();
      if (this.providerSettingsForm.get('maEnabled')?.dirty) {
        metadataValues.maDisabled = !rawValues.maEnabled;
      }
      if (this.providerSettingsForm.get('byoaEnabled')?.dirty) {
        metadataValues.byoaDisabled = !rawValues.byoaEnabled;
      }
    }
    const metadata = Object.keys(metadataValues).length > 0 ? JSON.stringify(metadataValues) : undefined;
    const fuelSurchargeRule = providerSettingValues?.fuelSurchargeRule || null;
    const fuelSurchargePercentage = providerSettingValues?.fuelSurchargePercentage || null;
    const fuelSurchargePerWeight = providerSettingValues?.fuelSurchargePerWeight || null;
    const enabled = providerSettingValues?.enabled !== undefined ? providerSettingValues.enabled : null;

    const params = {
      provider: this.provider.providerType,
      category: this.provider.category,
      markupPercentageValue,
      markupDollarValue,
      fuelSurchargeRule,
      fuelSurchargePercentage,
      fuelSurchargePerWeight,
      deliveryDate,
      ...(metadata !== undefined ? { metadata } : {}),
      enabled,
    };
    this.providersService.updateGlobalProvider(params).subscribe(
      (res) => {
        this.bannerNotificationsService.success('Global provider settings successfully updated');
        this.loading = false;
        this.drawerRef.dismiss(true);
      },
      (err) => {
        this.loading = false;
        this.bannerNotificationsService.error('Failed to update the global provider settings', {
          category: 'Updating global provider settings failed',
          message: err,
        });
      }
    );
  }

  public isValid(): boolean {
    const values = _.omit(this.providerSettingsForm.value, this.accountTypeSwitchControls);
    return !Object.values(values).every((value) => value === null || value === '') && this.providerSettingsForm.valid;
  }

  private setupForm(): void {
    const providerMarkupMarginPercent = _.has(this.provider, 'markupPercentageValue')
      ? this.provider.markupPercentageValue
      : null;
    const providerMarkupMarginAmount = _.has(this.provider, 'markupDollarValue')
      ? this.provider.markupDollarValue
      : null;
    const providerFuelSurchargePercentage = _.has(this.provider, 'fuelSurchargePercentage')
      ? this.provider.fuelSurchargePercentage
      : null;
    const providerFuelSurchargePerWeight = _.has(this.provider, 'fuelSurchargePerWeight')
      ? this.provider.fuelSurchargePerWeight
      : null;
    const providerFuelSurchargeRule = _.has(this.provider, 'fuelSurchargeRule')
      ? this.provider.fuelSurchargeRule
      : null;
    const providerDeliveryDate = _.has(this.provider, 'deliveryDate') ? this.provider.deliveryDate : null;
    const fuelSurchargeMarkupMargin = _.get(this.provider.metadata, 'fuelCharge', null);
    const providerEnabled = _.has(this.provider, 'enabled') ? this.provider.enabled : null;

    this.providerSettingsForm = this.formBuilder.group({
      markupPercentageValue: [providerMarkupMarginPercent, [Validators.max(MAX_MARKUP_PERCENT), Validators.min(MIN_MARKUP_VALUE)]],
      markupDollarValue: [providerMarkupMarginAmount, [Validators.max(MAX_MARKUP_DOLLAR), Validators.min(MIN_MARKUP_VALUE)]],
      fuelSurchargePercentage: [providerFuelSurchargePercentage, [Validators.max(MAX_MARKUP_DOLLAR), Validators.min(MIN_MARKUP_VALUE)]],
      fuelSurchargePerWeight: [providerFuelSurchargePerWeight, [Validators.max(MAX_MARKUP_DOLLAR), Validators.min(MIN_MARKUP_VALUE)]],
      fuelSurchargeRule: [providerFuelSurchargeRule],
      deliveryDate: [providerDeliveryDate, [Validators.max(10), Validators.min(0)]],
      fuelSurchargeMarkupMargin: [fuelSurchargeMarkupMargin, [Validators.max(100), Validators.min(0)]],
      enabled: [providerEnabled],
      maEnabled: [_.get(this.provider, 'metadata.maDisabled') !== true],
      byoaEnabled: [_.get(this.provider, 'metadata.byoaDisabled') !== true],
    });
  }
}
